import { Component, ViewChild, OnInit, OnDestroy, inject } from '@angular/core';
import { DashboardService } from '../dashboard/dashboard.service';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../admin/admin.service';
import isEmpty from 'lodash-es/isEmpty';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../services/environment.service';
import { SessionStorageService } from '../services/session-storage.service';
import { dashboardLinks } from '../lib/toolbar-links';
import { dashboardUtilityApps } from '../lib/utility-apps';
import { UtilityAppsService } from '../shared-components/utility-apps/utility-apps.service';

@Component({
  selector: 'app-rep-behavior',
  templateUrl: './rep-behavior.component.html',
  styleUrls: ['./rep-behavior.component.scss'],
  standalone: false,
})
export class RepBehaviorComponent implements OnInit, OnDestroy {
  private ss = inject(SessionStorageService);
  private dbs = inject(DashboardService);
  dialog = inject(MatDialog);
  private adminService = inject(AdminService);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);
  private snacky = inject(MatSnackBar);
  private uas = inject(UtilityAppsService);

  @ViewChild('repBehaviorGrid', { static: false }) repBehaviorGrid;

  private environment;
  private baseUrl: string;
  gridConfig = {
    title: 'Rep Behavior',
    gridName: 'RepBehavior',
    showGridFilters: true,
    showFilterManager: true,
    manualSearch: true,
    searchButtonText: 'Generate Report',
    showBulkActions: false,
    downloadGridButton: false,
    gridActionColumn: false,
    gridType: 'repBehavior',
    defaultEmptyGrid: true,
    simpleLoader: true,
    simpleLoaderText: 'Loading Report...',
    conditionalSearch: true,
    autoReports: true,
    groupByOptions: [
      { display: 'User', value: 'user' },
      { display: 'Rep', value: 'rep' },
    ],
    gridActions: [
      {
        icon: 'find_in_page',
        color: 'primary',
        text: 'Launch Case Explorer',
        action: this.launchCe,
      },
    ],
  };
  fileLoading = false;
  navCollapsed = false;
  dashboardLinks = dashboardLinks;

  ngOnInit(): void {
    this.navCollapsed = this.ss.get('utilityToolbarCollapsed');
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.uas.appsList.set(dashboardUtilityApps);
  }

  ngOnDestroy() {
    this.uas.appsList.set([]);
  }

  getGridSetup(configParam?: string) {
    if (!configParam) {
      configParam = 'Annuity Product Concentration-User';
    }
    return this.adminService.getUIConfigHeaders({
      uiconfig: 'rb-dash',
      type: configParam,
      implementation: 'rb',
    });
  }

  getRepBehavior(queryData) {
    const queryParams = JSON.parse(queryData);

    if (queryParams['search']['name']) {
      queryParams['name'] = queryParams['search']['name']['selected'][0];
      queryParams['search']['reportName'] = {
        equals: queryParams['search']['name']['selected'][0],
      };
      delete queryParams['search']['name'];
    }

    if (queryParams['search']['SavedTime']) {
      queryParams['search']['SavedTime']['start'] =
        queryParams['search']['SavedTime']['start'].split('T')[0];
      queryParams['search']['SavedTime']['end'] =
        queryParams['search']['SavedTime']['end'].split('T')[0];
    }

    if (isEmpty(queryParams['sort'])) {
      queryParams['sort'] = {};
    }

    return this.dbs.getDynamicReport(queryParams);
  }

  dlXlsx() {
    this.fileLoading = true;
    this.snacky.open(`File downloading. Please wait.`, 'Close', {
      duration: 4000,
    });
    const queryParams = this.repBehaviorGrid.generateQueryData();
    if (queryParams['search']['name']) {
      queryParams['name'] = queryParams['search']['name']['selected'][0];
      delete queryParams['search']['name'];
    }
    if (queryParams['name']) {
      queryParams['search']['reportName'] = { equals: queryParams['name'] };
    }

    if (queryParams['search']['SavedTime']) {
      queryParams['search']['SavedTime']['start'] =
        queryParams['search']['SavedTime']['start'].split('T')[0];
      queryParams['search']['SavedTime']['end'] =
        queryParams['search']['SavedTime']['end'].split('T')[0];
    }

    if (isEmpty(queryParams['sort'])) {
      queryParams['sort'] = {};
    }

    queryParams['take'] = 9999999;
    queryParams['skip'] = 0;

    this.dbs.getDynamicReportXlsx(queryParams).subscribe({
      next: data => {
        if (data) {
          const date = new Date();
          const filename = `${queryParams['name'] || 'Rep Behavior'} ${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}.xlsx`;
          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.fileLoading = false;
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
        }
      },
      error: () => {
        this.fileLoading = false;
        this.snacky.open(`An unexpected error was encountered.`, 'Close', {
          duration: 4000,
        });
      },
    });
  }

  launchCe(ev, dataItem, context) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const saveFilters = context.filter;

    saveFilters.filters = saveFilters.filters.filter(
      filter =>
        filter['field'] !== 'name' &&
        filter['field'] !== 'group' &&
        filter['field'] !== 'userId' &&
        !filter['filters']
    );

    if (context.groupBySelection === 'user') {
      saveFilters.filters.push({
        field: 'userId',
        operator: 'equal',
        value: dataItem.userId,
      });
    } else if (context.groupBySelection === 'rep') {
      saveFilters.filters.push({
        field: 'repCode',
        operator: 'equal',
        value: dataItem.repCode,
      });
    }

    const startIndex = saveFilters.filters.findIndex(
      filter => filter.operator === 'start'
    );

    context.ss.set('CaseExplorerFilters', saveFilters);
    context.ss.remove('RepBehaviorFilters');
    context.ss.remove('RepBehaviorSort');
    context.ss.remove('hiddenRepBehaviorColumns');

    context.route.navigate([`/case-explorer`]);
  }

  toggleNavCollapse() {
    this.navCollapsed = !this.navCollapsed;
  }
}
